import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
  Switch,
  Empty,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CompanyService } from '../../services/api/companyService';
import { CompanyInstanceService } from '../../services/api/companyInstanceService';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { BillingService } from '../../services/api/billingService';
import { Company, CompanyCreate, CompanyUpdate, CompanyFilters } from '../../types/company';
import { BillingStatus, CompanyStats } from '../../types/billing';
import { SplitPaneLayout } from '../common/SplitPaneLayout';
import { ManagementForm } from '../common/ManagementForm';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { COMPANY_MERGE_REFUSAL, duplicateCompanyNameGroups } from '../AppShell/platformDirectory';
import '../../styles/management-pages.css';
import styles from './CompanyManagement.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CompanyManagementProps {
  className?: string;
}

export const CompanyManagement: React.FC<CompanyManagementProps> = ({ className }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<CompanyFilters>({});
  const [companyStatsMap, setCompanyStatsMap] = useState<Record<string, { 
    userCount: number; 
    subscriptionCount: number; 
    billingStatus: BillingStatus;
    instanceCount?: number;
    artifactCount?: number;
    workflowCount?: number;
    artifactItemCount?: number;
    lastArtifactCreated?: string | null;
  }>>({});
  const navigate = useNavigate();

  const selectedCompany = selectedCompanyId 
    ? companies.find(c => c.id === selectedCompanyId) 
    : null;

  // Load companies on component mount and when filters change
  useEffect(() => {
    loadCompanies();
  }, [filters]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const companyList = await CompanyService.getAll(filters);
      setCompanies(companyList);
      
      // Pre-load company statistics for all companies
      const statsMap: Record<string, { 
        userCount: number; 
        subscriptionCount: number; 
        billingStatus: BillingStatus;
        instanceCount?: number;
        artifactCount?: number;
        workflowCount?: number;
        artifactItemCount?: number;
        lastArtifactCreated?: string | null;
      }> = {};
      
      await Promise.all(
        companyList.map(async (company) => {
          try {
            // Try to get stats from backend endpoint first
            let backendStats: CompanyStats | null = null;
            try {
              backendStats = await CompanyService.getCompanyStats(company.id);
            } catch {
              // Fallback to individual API calls if stats endpoint doesn't exist
            }
            
            // Extract artifact/workflow stats from backend response
            const instanceCount = backendStats?.instance_count ?? 0;
            const artifactCount = backendStats?.artifact_count ?? 0;
            const workflowCount = backendStats?.workflow_count ?? 0;
            const artifactItemCount = backendStats?.artifact_item_count ?? 0;
            const lastArtifactCreated = backendStats?.last_artifact_created ?? null;
            
            // Get user/subscription/billing stats (use backend if available, otherwise fallback)
            let userCount = backendStats?.user_count ?? 0;
            let subscriptionCount = backendStats?.subscription_count ?? 0;
            let billingStatus: BillingStatus = backendStats?.billing_status ?? 'No Billing Account';
            
            // Fallback: Get company instances count if not provided by backend
            if (userCount === 0 && !backendStats?.user_count) {
              try {
                const instances = await CompanyInstanceService.getAll({ company_id: company.id });
                userCount = instances.filter(inst => inst.is_active).length;
              } catch {
                userCount = 0;
              }
            }
            
            // Get subscription count if not provided by backend
            if (subscriptionCount === 0 && !backendStats?.subscription_count) {
              try {
                const companySubscriptions = await SubscriptionService.getCompanySubscriptions(company.id);
                subscriptionCount = companySubscriptions.filter(cs => cs.is_active).length;
              } catch {
                subscriptionCount = 0;
              }
            }
            
            // Get billing status if not provided by backend
            if (billingStatus === 'No Billing Account' && !backendStats?.billing_status) {
              try {
                billingStatus = await BillingService.getStatus(company.id);
              } catch {
                billingStatus = 'No Billing Account';
              }
            }
            
            statsMap[company.id] = {
              userCount,
              subscriptionCount,
              billingStatus,
              instanceCount,
              artifactCount,
              workflowCount,
              artifactItemCount,
              lastArtifactCreated,
            };
          } catch {
            statsMap[company.id] = {
              userCount: 0,
              subscriptionCount: 0,
              billingStatus: 'No Billing Account',
              instanceCount: 0,
              artifactCount: 0,
              workflowCount: 0,
              artifactItemCount: 0,
              lastArtifactCreated: null,
            };
          }
        })
      );
      setCompanyStatsMap(statsMap);
    } catch (err: any) {
      message.error(getApiErrorMessage(err, 'Failed to load companies'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = () => {
    // Create a temporary company object for the list
    const tempId = `temp-${Date.now()}`;
    const newCompany: Company = {
      id: tempId,
      name: '',
      description: '',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    // Add to beginning of list
    setCompanies([newCompany, ...companies]);
    setSelectedCompanyId(tempId);
    setIsCreating(true);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
    });
  };

  const handleSelectCompany = (company: Company) => {
    setSelectedCompanyId(company.id);
    setIsCreating(false);
    
    // Set form values
    form.setFieldsValue({
      name: company.name || '',
      description: company.description || '',
      industry: company.industry || undefined,
      size: company.size || undefined,
      website: company.website || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      is_active: company.is_active ?? true,
    });
  };

  const handleDeleteCompany = async (companyId: string) => {
    try {
      await CompanyService.delete(companyId);
      message.success('Company deleted successfully');
      await loadCompanies();
    } catch (err: any) {
      message.error(getApiErrorMessage(err, 'Failed to delete company'));
    }
  };

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      if (isCreating && selectedCompanyId?.startsWith('temp-')) {
        // Create new company
        const createData: CompanyCreate = {
          name: values.name?.trim() || '',
          ...(values.description?.trim() && { description: values.description.trim() }),
          ...(values.industry && { industry: values.industry }),
          ...(values.size && { size: values.size }),
          ...(values.website?.trim() && { website: values.website.trim() }),
          ...(values.address?.trim() && { address: values.address.trim() }),
          ...(values.phone?.trim() && { phone: values.phone.trim() }),
          ...(values.email?.trim() && { email: values.email.trim() }),
        };
        
        const newCompany = await CompanyService.create(createData);
        message.success('Company created successfully');
        
        // Remove temp item and add real one
        setCompanies(companies.filter(c => c.id !== selectedCompanyId).concat(newCompany));
        setSelectedCompanyId(newCompany.id);
        setIsCreating(false);
      } else if (selectedCompany) {
        // Update existing company
        const updateData: CompanyUpdate = {};
        
        if (values.name?.trim()) updateData.name = values.name.trim();
        if (values.description?.trim()) updateData.description = values.description.trim();
        if (values.industry) updateData.industry = values.industry;
        if (values.size) updateData.size = values.size;
        if (values.website?.trim()) updateData.website = values.website.trim();
        if (values.address?.trim()) updateData.address = values.address.trim();
        if (values.phone?.trim()) updateData.phone = values.phone.trim();
        if (values.email?.trim()) updateData.email = values.email.trim();
        if (values.is_active !== undefined) updateData.is_active = values.is_active;
        
        const updatedCompany = await CompanyService.update(selectedCompany.id, updateData);
        message.success('Company updated successfully');
        
        // Update in list
        setCompanies(companies.map(c => c.id === selectedCompany.id ? updatedCompany : c));
        setSelectedCompanyId(updatedCompany.id);
      }
      
      await loadCompanies();
    } catch (err: any) {
      console.error('Error saving company:', err);
      message.error(getApiErrorMessage(err, 'Failed to save company'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating && selectedCompanyId?.startsWith('temp-')) {
      // Remove temp item from list
      setCompanies(companies.filter(c => c.id !== selectedCompanyId));
    }
    setSelectedCompanyId(null);
    setIsCreating(false);
    form.resetFields();
  };

  const handleDelete = async () => {
    if (!selectedCompany) return;
    
    try {
      await CompanyService.delete(selectedCompany.id);
      message.success('Company deleted successfully');
      setSelectedCompanyId(null);
      setIsCreating(false);
      await loadCompanies();
    } catch (err: any) {
      message.error(getApiErrorMessage(err, 'Failed to delete company'));
      throw err;
    }
  };

  const handleFilterChange = (key: keyof CompanyFilters, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === undefined || value === null || value === '' ? undefined : value,
    }));
  };

  const duplicateCompanies = duplicateCompanyNameGroups(companies);
  const filteredCompanies = companies.filter(company => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      company.name.toLowerCase().includes(searchLower) ||
      company.description?.toLowerCase().includes(searchLower) ||
      company.industry?.toLowerCase().includes(searchLower) ||
      company.email?.toLowerCase().includes(searchLower)
    );
  });

  const getBillingStatusColor = (status: BillingStatus): string => {
    switch (status) {
      case 'Active':
        return 'green';
      case 'Past Due':
        return 'orange';
      case 'Suspended':
        return 'red';
      case 'No Billing Account':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, company: Company) => (
        <Button
          type="link"
          onClick={() => {
            navigate(`/company-management/${company.id}`);
          }}
          style={{ padding: 0 }}
        >
          <Text strong>{text}</Text>
        </Button>
      ),
      sorter: (a: Company, b: Company) => a.name.localeCompare(b.name),
    },
    {
      title: 'Industry',
      dataIndex: 'industry',
      key: 'industry',
      render: (text: string) => text || <Text type="secondary">Not set</Text>,
      sorter: (a: Company, b: Company) => (a.industry || '').localeCompare(b.industry || ''),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (text: string) => text || <Text type="secondary">Not set</Text>,
      sorter: (a: Company, b: Company) => (a.size || '').localeCompare(b.size || ''),
    },
    {
      title: 'Users',
      key: 'users',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const count = stats?.userCount || 0;
        return <Text>{count}</Text>;
      },
      sorter: (a: Company, b: Company) => {
        const aCount = companyStatsMap[a.id]?.userCount || 0;
        const bCount = companyStatsMap[b.id]?.userCount || 0;
        return aCount - bCount;
      },
    },
    {
      title: 'Subscriptions',
      key: 'subscriptions',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const count = stats?.subscriptionCount || 0;
        return <Text>{count}</Text>;
      },
      sorter: (a: Company, b: Company) => {
        const aCount = companyStatsMap[a.id]?.subscriptionCount || 0;
        const bCount = companyStatsMap[b.id]?.subscriptionCount || 0;
        return aCount - bCount;
      },
    },
    {
      title: 'Billing Status',
      key: 'billing_status',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const status = stats?.billingStatus || 'No Billing Account';
        return (
          <Tag color={getBillingStatusColor(status)}>
            {status}
          </Tag>
        );
      },
      sorter: (a: Company, b: Company) => {
        const aStatus = companyStatsMap[a.id]?.billingStatus || 'No Billing Account';
        const bStatus = companyStatsMap[b.id]?.billingStatus || 'No Billing Account';
        return aStatus.localeCompare(bStatus);
      },
    },
    {
      title: 'Instances',
      key: 'instances',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const count = stats?.instanceCount ?? 0;
        return <Text>{count}</Text>;
      },
      sorter: (a: Company, b: Company) => {
        const aCount = companyStatsMap[a.id]?.instanceCount ?? 0;
        const bCount = companyStatsMap[b.id]?.instanceCount ?? 0;
        return aCount - bCount;
      },
    },
    {
      title: 'Artifacts',
      key: 'artifacts',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const count = stats?.artifactCount ?? 0;
        return <Text>{count}</Text>;
      },
      sorter: (a: Company, b: Company) => {
        const aCount = companyStatsMap[a.id]?.artifactCount ?? 0;
        const bCount = companyStatsMap[b.id]?.artifactCount ?? 0;
        return aCount - bCount;
      },
    },
    {
      title: 'Workflows',
      key: 'workflows',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const count = stats?.workflowCount ?? 0;
        return <Text>{count}</Text>;
      },
      sorter: (a: Company, b: Company) => {
        const aCount = companyStatsMap[a.id]?.workflowCount ?? 0;
        const bCount = companyStatsMap[b.id]?.workflowCount ?? 0;
        return aCount - bCount;
      },
    },
    {
      title: 'Artifact Items',
      key: 'artifact_items',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const count = stats?.artifactItemCount ?? 0;
        return <Text>{count}</Text>;
      },
      sorter: (a: Company, b: Company) => {
        const aCount = companyStatsMap[a.id]?.artifactItemCount ?? 0;
        const bCount = companyStatsMap[b.id]?.artifactItemCount ?? 0;
        return aCount - bCount;
      },
    },
    {
      title: 'Last Artifact',
      key: 'last_artifact',
      render: (company: Company) => {
        const stats = companyStatsMap[company.id];
        const lastCreated = stats?.lastArtifactCreated;
        if (!lastCreated) {
          return <Text type="secondary">Never</Text>;
        }
        try {
          const date = new Date(lastCreated);
          return <Text>{date.toLocaleDateString()}</Text>;
        } catch {
          return <Text type="secondary">Invalid date</Text>;
        }
      },
      sorter: (a: Company, b: Company) => {
        const aDate = companyStatsMap[a.id]?.lastArtifactCreated;
        const bDate = companyStatsMap[b.id]?.lastArtifactCreated;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(aDate).getTime() - new Date(bDate).getTime();
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      sorter: (a: Company, b: Company) => (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (company: Company) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleSelectCompany(company)}
            title="Edit company"
          />
          <Popconfirm
            title="Are you sure you want to delete this company?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteCompany(company.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete company"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeCompanies = companies.filter(company => company.is_active).length;
  const totalCompanies = companies.length;

  const formTabs = [
    {
      key: 'basic',
      label: 'Basic Info',
      children: (
        <>
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter company description" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="industry" label="Industry">
                <Select placeholder="Select industry" allowClear>
                  <Option value="Technology">Technology</Option>
                  <Option value="Finance">Finance</Option>
                  <Option value="Healthcare">Healthcare</Option>
                  <Option value="Retail">Retail</Option>
                  <Option value="Manufacturing">Manufacturing</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="size" label="Company Size">
                <Select placeholder="Select size" allowClear>
                  <Option value="Small">Small</Option>
                  <Option value="Medium">Medium</Option>
                  <Option value="Large">Large</Option>
                  <Option value="Enterprise">Enterprise</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      children: (
        <>
          <Form.Item name="website" label="Website">
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" placeholder="contact@example.com" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="+1 (555) 123-4567" />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <TextArea rows={2} placeholder="Enter company address" />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'settings',
      label: 'Settings',
      children: (
        <>
          {!isCreating && (
            <Form.Item name="is_active" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}
        </>
      ),
    },
  ];

  return (
    <div className={`management-page-container ${className || ''}`}>
      <div className="management-page-header">
        <Title level={2}>Company Management</Title>
        <Text type="secondary">Manage companies and their settings</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Companies"
              value={totalCompanies}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Companies"
              value={activeCompanies}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive Companies"
              value={totalCompanies - activeCompanies}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <SplitPaneLayout
        leftContent={
          <Card
            className="list-card"
            title={
              <Space>
                <Title level={4} style={{ margin: 0 }}>Companies</Title>
                <Tag>{filteredCompanies.length}</Tag>
              </Space>
            }
            extra={
              <Space>
                <Input
                  placeholder="Search companies..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <Button icon={<ReloadOutlined />} onClick={loadCompanies} size="small">
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateCompany}
                >
                  New
                </Button>
              </Space>
            }
          >
            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Select
                placeholder="Filter by Industry"
                style={{ width: 150 }}
                allowClear
                onChange={(value) => handleFilterChange('industry', value)}
                value={filters.industry}
                size="small"
              >
                <Option value="Technology">Technology</Option>
                <Option value="Finance">Finance</Option>
                <Option value="Healthcare">Healthcare</Option>
                <Option value="Retail">Retail</Option>
                <Option value="Manufacturing">Manufacturing</Option>
                <Option value="Other">Other</Option>
              </Select>
              <Select
                placeholder="Filter by Size"
                style={{ width: 150 }}
                allowClear
                onChange={(value) => handleFilterChange('size', value)}
                value={filters.size}
                size="small"
              >
                <Option value="Small">Small</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Large">Large</Option>
                <Option value="Enterprise">Enterprise</Option>
              </Select>
              <Select
                placeholder="Filter by Status"
                style={{ width: 120 }}
                allowClear
                onChange={(value) => handleFilterChange('is_active', value)}
                value={filters.is_active}
                size="small"
              >
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </div>
            {duplicateCompanies.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${duplicateCompanies.length} company name${duplicateCompanies.length === 1 ? '' : 's'} appear more than once`}
                description={COMPANY_MERGE_REFUSAL}
              />
            ) : null}
            <Table
              columns={columns.filter(col => col.key !== 'actions')}
              dataSource={filteredCompanies}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} companies`,
                position: ['bottomCenter'],
              }}
              scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }}
              onRow={(record) => ({
                onClick: () => navigate(`/company-management/${record.id}`),
                style: {
                  cursor: 'pointer',
                  opacity: record.is_active === false ? 0.55 : 1,
                },
              })}
              rowClassName={(record) => (record.is_active === false ? 'inactive-company-row' : '')}
            />
          </Card>
        }
        rightContent={
          isCreating ? (
            <ManagementForm
              title="New Company"
              isCreating
              isLoading={false}
              isSaving={isSaving}
              form={form}
              tabs={formTabs}
              onSave={handleSave}
              onCancel={handleCancel}
              showDelete={false}
            />
          ) : (
            <Card className="details-card">
              <Empty
                description="Open a company from the list. Profile, users, subscriptions, billing, and product models are on that page. Click New to create a company."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )
        }
      />
    </div>
  );
};
